/**
 * TreeSelectComponent
 * Hierarchical tree-based select component with lazy loading
 */

import React, { useCallback, useMemo, useState, memo } from 'react';
import type { FormControlProps } from '../../types';

export interface TreeNode {
  id: string;
  label: string;
  children?: TreeNode[];
  disabled?: boolean;
}

interface TreeSelectComponentProps extends FormControlProps {
  dataSource?: TreeNode[];
  multiple?: boolean;
  checkable?: boolean;
  expandable?: boolean;
  lazy?: boolean;
  lazyLoadUrl?: string;
  showSearch?: boolean;
  defaultExpandAll?: boolean;
  onChange?: (selectedIds: string | string[]) => void;
}

interface TreeNodeProps {
  node: TreeNode;
  level: number;
  selected: Set<string>;
  expanded: Set<string>;
  multiple: boolean;
  checkable: boolean;
  expandable: boolean;
  onSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
  disabled?: boolean;
}

const TreeNodeItem: React.FC<TreeNodeProps> = memo(({
  node,
  level,
  selected,
  expanded,
  multiple,
  checkable,
  expandable,
  onSelect,
  onToggleExpand,
  disabled: parentDisabled = false,
}) => {
  const isDisabled = parentDisabled || node.disabled;
  const isSelected = selected.has(node.id);
  const isExpanded = expanded.has(node.id);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="tree-select__node" style={{ paddingLeft: `${level * 16}px` }}>
      <div className="tree-select__node-content">
        {expandable && hasChildren && (
          <button
            type="button"
            onClick={() => onToggleExpand(node.id)}
            className={`tree-select__expand-button ${isExpanded ? 'tree-select__expand-button--expanded' : ''}`}
            aria-expanded={isExpanded}
            disabled={isDisabled}
          >
            ▶
          </button>
        )}

        {expandable && !hasChildren && (
          <div className="tree-select__expand-placeholder" />
        )}

        {checkable && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(node.id)}
            disabled={isDisabled}
            className="tree-select__checkbox"
            aria-label={`Select ${node.label}`}
          />
        )}

        {!checkable && (
          <button
            type="button"
            onClick={() => onSelect(node.id)}
            disabled={isDisabled}
            className={`tree-select__label ${isSelected ? 'tree-select__label--selected' : ''}`}
            aria-pressed={isSelected}
          >
            {node.label}
          </button>
        )}

        {checkable && (
          <label className="tree-select__label">
            {node.label}
          </label>
        )}
      </div>

      {isExpanded && hasChildren && (
        <div className="tree-select__children">
          {node.children!.map((child) => (
            <TreeNodeItem
              key={child.id}
              node={child}
              level={level + 1}
              selected={selected}
              expanded={expanded}
              multiple={multiple}
              checkable={checkable}
              expandable={expandable}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              disabled={isDisabled}
            />
          ))}
        </div>
      )}
    </div>
  );
});

TreeNodeItem.displayName = 'TreeNodeItem';

export const TreeSelectComponent: React.FC<TreeSelectComponentProps> = memo((props) => {
  const {
    value,
    onChange,
    dataSource = [],
    multiple = false,
    checkable = false,
    expandable = true,
    disabled = false,
  } = props;

  const [expanded, setExpanded] = useState<Set<string>>(
    new Set(props.defaultExpandAll ? dataSource.map((node) => node.id) : [])
  );
  const selected = useMemo(
    () => new Set(Array.isArray(value) ? value : value ? [value] : []),
    [value]
  );

  const handleSelect = useCallback(
    (id: string) => {
      const newSelected = new Set(selected);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else if (!multiple) {
        newSelected.clear();
      }
      newSelected.add(id);
      const result = multiple ? Array.from(newSelected) : Array.from(newSelected)[0];
      onChange?.(result);
    },
    [selected, multiple, onChange]
  );

  const handleToggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <div
      className="af-tree-select af-tree-select__container"
      role="tree"
      aria-disabled={disabled}
    >
      {dataSource.map((node) => (
        <TreeNodeItem
          key={node.id}
          node={node}
          level={0}
          selected={selected}
          expanded={expanded}
          multiple={multiple}
          checkable={checkable}
          expandable={expandable}
          onSelect={handleSelect}
          onToggleExpand={handleToggleExpand}
          disabled={disabled}
        />
      ))}
    </div>
  );
});

TreeSelectComponent.displayName = 'TreeSelectComponent';

export default TreeSelectComponent;
